import { Button, Checkbox, FormControlLabel, Grid, ListItem } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';

import { ISQLOption } from '~/src/dash/utils/graph';

// since the keys to the SQL field do not necessarily match what's returned by the graph,
// we will use an object to correctly map these fields over ONLY IF they differ
// ex: `fillrate` in `keyToSQLField` corresponds to `fill_rate` in graph `40`
const mapKeyToGraph: { [key: string]: string } = {
  fillrate: 'fill_rate',
};

const reverseMapKeyToGraph: { [key: string]: string } = {
  fill_rate: 'fillrate',
};

interface ISelectOrderByItem extends ISQLOption {
  checked: boolean;
  ascending: boolean;
}

interface ISelectOrderByProps {
  items: ISQLOption[];
  reportOrderBy: string;
  handleChangeOrderBy: (orderBy: string) => void;
}

export function SelectOrderByView({
  items,
  handleChangeOrderBy,
  reportOrderBy,
}: ISelectOrderByProps) {
  const [orderByItems, setOrderByItems] = useState<ISelectOrderByItem[]>([]);

  // whenever the metrics change, reset the state and update the status of items
  // to reflect what's already present in the `orderByItems` state
  useEffect(() => {
    const mappedItems: ISelectOrderByItem[] = items.map((item) => ({
      key: item.key,
      text: item.text,
      checked: false,
      ascending: true,
    }));
    if (orderByItems && orderByItems.length) {
      for (let idx = 0; idx < mappedItems.length; idx++) {
        for (let jdx = 0; jdx < orderByItems.length; jdx++) {
          if (mappedItems[idx].key === orderByItems[jdx].key) {
            mappedItems[idx].checked = orderByItems[jdx].checked;
            mappedItems[idx].ascending = orderByItems[jdx].ascending;
            break;
          }
        }
      }
    }
    setOrderByItems(mappedItems);
  }, [JSON.stringify(items)]);

  // when the component is mounted, we want to load the order by state from the db
  // the order of these useEffects matter - moving this to the top of the function
  // makes it so that the order by options do not correctly render on the page
  useEffect(() => {
    const mappedItems: ISelectOrderByItem[] = items.map((item) => ({
      key: item.key,
      text: item.text,
      checked: false,
      ascending: true,
    }));
    if (reportOrderBy && reportOrderBy.length) {
      const orderByFields = reportOrderBy.split(',');
      for (let idx = 0; idx < orderByFields.length; idx++) {
        const splitKeyVal = orderByFields[idx].split(' ');
        if (reverseMapKeyToGraph[splitKeyVal[0]] !== undefined) {
          splitKeyVal[0] = reverseMapKeyToGraph[splitKeyVal[0]];
        }
        for (let jdx = 0; jdx < mappedItems.length; jdx++) {
          if (splitKeyVal[0] === mappedItems[jdx].key) {
            mappedItems[jdx].checked = true;
            mappedItems[jdx].ascending = splitKeyVal[1] === 'ASC' ? true : false;
            break;
          }
        }
      }
    }
    setOrderByItems(mappedItems);
  }, []);

  // updates the `ReportOrderBy` property for the state in `bidder-report-form-view.tsx`
  useEffect(() => {
    handleChangeOrderBy(parseItems(orderByItems));
  }, [orderByItems]);

  // `parseItems` will take the current state and convert this into a string under the
  // reportOrderBy property of the call to the console api
  const parseItems = (orderByItems: ISelectOrderByItem[]): string => {
    const filteredItems = orderByItems.filter((orderByItem) => orderByItem.checked);
    return filteredItems
      .map((filteredItem) => {
        if (mapKeyToGraph[filteredItem.key] !== undefined) {
          return filteredItem.ascending
            ? `${mapKeyToGraph[filteredItem.key]} ASC`
            : `${mapKeyToGraph[filteredItem.key]} DESC`;
        }
        return filteredItem.ascending ? `${filteredItem.key} ASC` : `${filteredItem.key} DESC`;
      })
      .join(',');
  };

  // clone the order by items in state and toggle a single metric option's checked status
  const handleChangeItemChecked = (idx: number) => {
    const newState = cloneDeep(orderByItems);
    newState[idx].checked = !newState[idx].checked;
    setOrderByItems(newState);
  };

  // clone the order by items in state and toggle a single metric option's ascending status
  const handleChangeItemAsc = (idx: number) => {
    const newState = cloneDeep(orderByItems);
    newState[idx].ascending = !newState[idx].ascending;
    setOrderByItems(newState);
  };

  return orderByItems && orderByItems.length ? (
    <Grid
      style={{
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: 0,
      }}
    >
      {orderByItems.map((orderByItem: ISelectOrderByItem, idx: number) => (
        <ListItem
          style={{
            display: 'inline-flex',
            flexDirection: 'row',
            width: '500px',
            paddingLeft: 0,
          }}
        >
          <FormControlLabel
            style={{ width: '100%' }}
            control={
              <Checkbox
                checked={orderByItem.checked}
                onClick={() => handleChangeItemChecked(idx)}
              />
            }
            label={orderByItem.text}
          />
          <Button onClick={() => handleChangeItemAsc(idx)}>
            {orderByItem.ascending ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </Button>
        </ListItem>
      ))}
    </Grid>
  ) : (
    <span>Select metrics to order by</span>
  );
}
